import React, { useState } from 'react';
import { ReactComponent as FilterIcon } from '../../../assets/PepWise2.0/Filter - Icon - New.svg';
import { Box, IconButton, Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';

type FilterOption = string | { name: string; isAllowed?: boolean } | { label: string; value: string };

type SingleSelectFilterProps = {
    sectorName: string;
    setSectorName: React.Dispatch<React.SetStateAction<string>>;
    sectorOptionsList: FilterOption[];
    handleChange?: (value: string) => void;
    boxProps?: any;
    hasNotProvided?: boolean;
};

const SingleSelectFilter = ({ 
    sectorName,
    setSectorName,
    sectorOptionsList,
    handleChange,
    boxProps,
    hasNotProvided = false
}: SingleSelectFilterProps) => {
    const theme = useTheme();
    const colors = theme.palette as any;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const open = Boolean(anchorEl);
    const disabled = boxProps?.disabled;

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return;
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSectorChange = (value: string) => {
        setSectorName(value);
        handleChange?.(value);
        handleClose();
    };

    const normalizedOptions = sectorOptionsList.map((option: any) => {
        if (typeof option === 'string') {
            return { label: option.toUpperCase(), value: option };
        }
        if ('name' in option) {
            return {
                label: option.name.toUpperCase(),
                value: option.name,
                isDisabled: option.isAllowed === false,
            };
        }
        return { label: option.label?.toUpperCase(), value: option.value };
    });

    const hasValue = (val: string) =>
        normalizedOptions.some(opt => opt.value === val);

    const options = [
        ...(hasValue('all')
            ? []
            : [{ label: 'ALL SECTORS', value: 'all' }]),
        ...(hasNotProvided && !hasValue('Not Provided')
            ? [{ label: 'NOT PROVIDED', value: 'Not Provided' }]
            : []),
        ...normalizedOptions,
    ];

    const selectedOption = options.find((option: any) => option.value === sectorName);
    const toTitleCase = (value: string) =>
        value
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    const selectedLabel = selectedOption?.label === "ALL SECTORS"
        ? toTitleCase(selectedOption.label)
        : selectedOption?.label;

    const triggerColor = open
        ? colors.DropdownV2?.dropdownIconActive
        : colors.DropdownV2?.dropdownIconDefault;

    const selectedColor = colors.DropdownV2.menuItemSelectedText;
    const hoverColor = colors.DropdownV2.menuItemHover;
    const activeCheckIconColor = colors.DropdownV2.tickColor;
    const cardBg = colors.DropdownV2.menuItemBackground;
    const disabledColor = colors.DropdownV2.mutedText;

    return (
        <Box
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                ...boxProps?.sx
            }}
        >
            <Typography
                onClick={handleOpen}
                sx={{
                    color: triggerColor,
                    fontFamily: 'Montserrat',
                    fontSize: '14px',
                    fontWeight: 400,
                    cursor: disabled ? 'default' : 'pointer',
                    userSelect: 'none',
                    opacity: disabled ? 0.5 : 1,
                    whiteSpace: 'nowrap'
                }}
            >
                {selectedLabel}
            </Typography>

            <IconButton
                onClick={handleOpen}
                disabled={disabled}
                sx={{
                    width: 28,
                    height: 28,
                    p: 0,
                    color: triggerColor,
                    '& svg': {
                        color: triggerColor,
                    },
                    '& svg path, & svg line, & svg circle, & svg rect': {
                        stroke: `${triggerColor} !important`,
                    },
                    '& svg path[fill], & svg circle[fill], & svg rect[fill]': {
                        fill: `${triggerColor} !important`,
                    },
                    '&.Mui-disabled': {
                        opacity: 0.5
                    }
                }}
            >
                <FilterIcon />
            </IconButton>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                PaperProps={{
                    sx: {
                        minWidth: 220,
                        mt: 1,
                        backgroundColor: cardBg,
                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                        borderRadius: '4px',
                    }
                }}
                MenuListProps={{
                    sx: {
                        p: 0,
                        backgroundColor: `${cardBg} !important`
                    }
                }}
            >
                {options.map((option: any) => {
                    const isSelected = sectorName === option.value;

                    return (
                        <MenuItem
                            key={option.value}
                            disabled={option.isDisabled}
                            selected={isSelected}
                            onClick={() => handleSectorChange(option.value)}
                            sx={{
                                minHeight: 38,
                                px: 2,
                                gap: 2,
                                backgroundColor: `${cardBg} !important`,
                                color: `${selectedColor} !important`,
                                fontFamily: 'Montserrat',
                                fontSize: '14px',
                                fontWeight: isSelected ? 500 : 400,

                                '&:hover': {
                                    backgroundColor: `${hoverColor} !important`,
                                    color: `${colors.DropdownV2.menuItemHoverText} !important`,

                                    '& .MuiSvgIcon-root': {
                                        color: `${activeCheckIconColor} !important`,
                                    }
                                },

                                '&.Mui-selected': {
                                    backgroundColor: `${colors.DropdownV2.menuItemSelectedBg} !important`,
                                    color: `${selectedColor} !important`
                                },

                                '&.Mui-selected:hover': {
                                    backgroundColor: `${hoverColor} !important`,
                                    color: `${colors.DropdownV2.menuItemSelectedText} !important`,
                                },

                                '&.Mui-disabled': {
                                    color: `${disabledColor} !important`,
                                    opacity: '1 !important'
                                }
                            }}
                        >
                            <ListItemText
                                primary={option.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontFamily: 'Montserrat',
                                        fontSize: '14px',
                                        fontWeight: isSelected ? 500 : 400
                                    }
                                }}
                            />

                            {isSelected && (
                                <CheckIcon
                                    sx={{
                                        fontSize: 18,
                                        color: `${activeCheckIconColor} !important`,
                                        ml: 'auto'
                                    }}
                                />
                            )}
                        </MenuItem>
                    );
                })}
            </Menu>
        </Box>
    );
};

export default SingleSelectFilter;